import { ReactNode, useCallback, memo, useState } from 'react'
import { Box, Flex, useDisclosure, useMediaQuery } from '@chakra-ui/react'
import { useAuthStore } from '@/store/auth'
import { useNavigate } from 'react-router-dom'
import { Header } from '@/components/layout/Header'
import { Sidebar } from '@/components/layout/Sidebar'
import { ContentArea } from '@/components/layout/ContentArea'
import { UnsavedChangesLeaveDialog } from '@/components/layout/UnsavedChangesLeaveDialog'
import { PhoneWidget } from '@/components/helpdesk/PhoneWidget'
import { useThemeColors } from '@/hooks/useThemeColors'

interface AdminLayoutProps {
  children: ReactNode
}

function AdminLayoutComponent({ children }: AdminLayoutProps) {
  const bgColor = useThemeColors().bg

  //Mobile drawer controller (overlay)
  const mobileDrawer = useDisclosure()

  // Sidebar starts toggled (collapsed) when user is logged in
  const [isCollapsed, setIsCollapsed] = useState(true)

  const [isMobile] = useMediaQuery('(max-width: 768px)')

  const user = useAuthStore((state) => state.user)
  const logout = useAuthStore((state) => state.logout)
  const navigate = useNavigate()

  const handleLogout = useCallback(async (retainTicketIds?: number[]) => {
    await logout(retainTicketIds)
    navigate('/login')
  }, [logout, navigate])

  const handleSidebarClose = useCallback(() => {
    mobileDrawer.onClose()
  }, [mobileDrawer])

  const handleSidebarOpen = useCallback(() => {
    if (isMobile) {
      mobileDrawer.onOpen() // Mobile = Drawer overlay
    } else {
      setIsCollapsed((prev) => !prev) // Desktop = collapse/push
    }
  }, [isMobile, mobileDrawer])

  return (
  <Box h="100vh" overflow="hidden" bg={bgColor}>
    <Flex h="100vh" overflow="hidden">
      <Sidebar
        isOpen={mobileDrawer.isOpen}
        onClose={handleSidebarClose}
        isCollapsed={isCollapsed}
      />

      {/* Main Content */}
      <Box
        flex="1"
        bg={bgColor}
        display="flex"
        flexDirection="column"
        minW={0}
        h="100vh"
        overflow="hidden"
      >
        <Header onOpen={handleSidebarOpen} user={user} onLogout={handleLogout} />

        <ContentArea>{children}</ContentArea>
      </Box>
    </Flex>
    {user ? <PhoneWidget /> : null}
    <UnsavedChangesLeaveDialog />
  </Box>
)

}

export const AdminLayout = memo(AdminLayoutComponent)
